/** @jsx isolateComponent */

import Downshift from 'downshift';
import { ReactNode } from 'react';
import { omit } from 'lodash';

import { Colors, Icon } from '@zapier/design-system';

import { Input, InputProps, InputChildrenAttrs } from './Input';

import { camelCaseAriaAttrs } from './Input/utils';

import { isolateComponent } from '@zapier/style-encapsulation';

import {
  FloatingMenu,
  FloatingMenuGetLabelForItem,
  FloatingMenuItem,
} from './FloatingMenu';

import { labelStyles, withModifier } from './utils';

export type DropdownProps = {
  /**
   * Callback that will be used to generate the label to be rendered for
   * each item on the menu list. Supports any valid React Node. Defaults to the
   * value of `getLabelForSelectedItem`.
   */
  getLabelForListItems?: (item: FloatingMenuItem) => ReactNode;
  /**
   * Callback that will be used to generate the string label to be rendered for
   * the selected dropdown item.
   */
  getLabelForSelectedItem: FloatingMenuGetLabelForItem;
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.  Defaults to the index of each item in the array.
   */
  getKeyForItem?: (item: FloatingMenuItem) => string;
  /**
   * HTML `id` attribute for the `button` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string;
  /**
   * The list of items.
   */
  items: Array<FloatingMenuItem>;
  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  menuAriaLabel: string;
  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange: (item: FloatingMenuItem) => void;
  /**
   * Optional prop for setting the initially selected item, but still letting `Downshift` manage
   * the selected item state for you.
   */
  initialSelectedItem?: FloatingMenuItem;
  /**
   * A render prop which receives a menu `item` and returns a React element,
   * which is an icon illustrating the given menu item. The natural size for
   * the icon is 30x30px. If not provided, no icons will be rendered.
   */
  renderIcon?: (item: FloatingMenuItem) => ReactNode;
  /**
   * Optional prop for setting the selected item for a controlled component
   */
  selectedItem?: FloatingMenuItem;
} & Omit<InputProps, 'children' | 'onChange'>;

const isDisabled = (props: DropdownProps): boolean => {
  return (props.isDisabled as boolean) || (props.items.length as number) === 0;
};

const Styles = labelStyles(__filename, {
  buttonText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as 'nowrap',
  },

  buttonWrap: (props: DropdownProps) => ({
    cursor: (isDisabled(props) as boolean) ? 'default' : 'pointer',
  }),

  placeholder: (
    props: DropdownProps,
    derivedStated: {
      isHovered: boolean;
      isFocused: boolean;
    }
  ) => [
    // Note that the order of these array items is important for specificity.
    {
      color: Colors.gray80,
    },
    (!isDisabled as boolean) &&
      (derivedStated.isHovered || derivedStated.isFocused) &&
      withModifier('hovered-or-focused', {
        color: Colors.ocean,
      }),
    props.isErrored &&
      withModifier('errored', {
        color: Colors.lava,
      }),
    (isDisabled(props) as boolean) &&
      withModifier('disabled', {
        color: Colors.gray80,
      }),
  ],

  root: {
    position: 'relative' as 'relative',
  },
});

const buildAriaLabel = ({
  label,
  value,
  placeholder,
}: {
  label: string;
  value?: string;
  placeholder?: string;
}) => {
  return [value, label, !value && placeholder].filter(Boolean).join(', ');
};

/**
 * A customization of the `Dropdown` component from the Universal Library that supports HTML in list items.
 */
const Dropdown = (props: DropdownProps) => {
  const itemToString = (item?: FloatingMenuItem): any => {
    return item ? props.getLabelForSelectedItem(item) : '';
  };

  const getLabelForListItems =
    props.getLabelForListItems || props.getLabelForSelectedItem;

  return (
    <Downshift
      inputId={props.inputId}
      itemCount={props.items.length}
      itemToString={itemToString}
      onChange={props.onChange}
      // Apply either the selectedItem (controlled) or initialSelectedItem prop (uncontrolled)
      {...(props.selectedItem
        ? { selectedItem: props.selectedItem }
        : { initialSelectedItem: props.initialSelectedItem })}
    >
      {({
        closeMenu,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        highlightedIndex,
        isOpen,
        openMenu,
        selectedItem,
      }) => {
        const hasSelectedItem = !!selectedItem;

        const renderIcon = () =>
          typeof props.renderIcon === 'function'
            ? props.renderIcon(selectedItem)
            : undefined;

        // Remove some DOM attrs that axe complains about.
        // These should be redundant given `ariaLabel` and `inputId`.
        const rootProps = omit(getRootProps(), ['aria-labelledby', 'role']);

        return (
          <div css={Styles.root} {...rootProps}>
            <div css={Styles.buttonWrap(props)}>
              <Input
                {...props}
                {...camelCaseAriaAttrs(
                  getInputProps({
                    onKeyDown: (event: {
                      key: string;
                      nativeEvent: {
                        preventDownshiftDefault: boolean;
                      };
                    }) => {
                      if (event.key === 'Escape') {
                        event.nativeEvent.preventDownshiftDefault = true;
                        closeMenu();
                      }
                    },
                  })
                )}
                canClickIconAfter={false}
                id={props.inputId}
                isDisabled={isDisabled(props) as boolean}
                renderIconAfter={() => (
                  <Icon name="upAndDownArrows" size={24} />
                )}
                renderIconBefore={renderIcon}
              >
                {(inputProps: InputChildrenAttrs, derivedState: any) => (
                  // Intentionally `button` and not `Button` since it looks
                  // and behaves differently.
                  <button
                    {...{
                      // `Downshift` requires this attribute to work properly, so
                      // there's an a11y balance to perform here. This is spread as
                      // an object since the `eslint-disable-next-line` comment didn't
                      // work for JSX props.
                      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
                      'aria-activedescendant':
                        inputProps['aria-activedescendant'],
                    }}
                    aria-controls={inputProps['aria-controls']}
                    aria-expanded={isOpen ? 'true' : 'false'}
                    aria-haspopup="true"
                    aria-invalid={inputProps['aria-invalid']}
                    aria-label={buildAriaLabel({
                      label: inputProps['aria-label'] || '',
                      placeholder: props.placeholder,
                      value: hasSelectedItem
                        ? (props.getLabelForSelectedItem(
                            selectedItem
                          ) as string)
                        : undefined,
                    })}
                    css={[inputProps.css]}
                    disabled={inputProps.disabled}
                    id={inputProps.id}
                    onBlur={closeMenu as any}
                    onChange={props.onChange}
                    onClick={isDisabled(props) ? undefined : (openMenu as any)}
                    onKeyDown={inputProps.onKeyDown as any}
                  >
                    {hasSelectedItem ? (
                      <span css={Styles.buttonText}>
                        {props.getLabelForSelectedItem(selectedItem)}
                      </span>
                    ) : (
                      <span
                        css={[
                          Styles.buttonText,
                          Styles.placeholder(props, derivedState),
                        ]}
                      >
                        {props.placeholder}
                      </span>
                    )}
                  </button>
                )}
              </Input>
            </div>
            {isOpen && (
              <FloatingMenu
                ariaLabel={props.menuAriaLabel}
                getItemProps={getItemProps}
                getKeyForItem={props.getKeyForItem}
                getLabelForItem={getLabelForListItems}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex as any}
                items={props.items}
                renderIcon={props.renderIcon}
              />
            )}
          </div>
        );
      }}
    </Downshift>
  );
};

Dropdown.defaultProps = {
  color: 'gray10',
  size: 'small',
};

export { Dropdown };
